import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SDAGoBack from "./SDAGoBack";
import SDASteps from "./SDASteps";
import { SDATestModeBanner, SDATestSkipButton } from "./sdaTestMode";
import {
  PaymentMethodPicker,
  AddCardModal,
  AddBankModal,
} from "../../components/payment";
import { usePaymentMethods } from "../../hooks/usePaymentMethods";
import { useSDAInvite } from "./SDAContext";
import { purchaseSdaInvite, SDAPurchaseResult } from "../../api/services";
import { allowedPaymentMethodType } from "@libs/quote";
import { formatCurrency } from "../../utils/formatCurrency";
import SDASuccess from "./SDASuccess";

const SDAPayment: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { invite, quote } = useSDAInvite();
  const {
    methods,
    loading: methodsLoading,
    refresh: refreshMethods,
  } = usePaymentMethods();
  const [selectedPayment, setSelectedPayment] = useState<number | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [result, setResult] = useState<SDAPurchaseResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);
  // ACH authorization acceptance. Required server-side for any
  // `us_bank_account` PM; legacy accounts attached without the Plaid+
  // Stripe link flow have no stored Nacha mandate and Stripe rejects
  // the charge otherwise. Reset to false whenever the user picks a
  // different PM — re-selecting a bank should require a fresh
  // acknowledgment.
  const [mandateAccepted, setMandateAccepted] = useState(false);
  // setState isn't synchronous, so a fast double-click can fire two requests
  // before `purchasing` flips. The ref blocks the second one immediately.
  const inFlightRef = useRef(false);

  // Exactly one method type is valid, depending on how soon coverage
  // starts: card inside 5 days (ACH wouldn't clear in time), bank outside
  // it. Shared with the renewal flow and enforced server-side — see
  // @libs/quote payment-timing.
  const allowedType = allowedPaymentMethodType(
    quote?.coverageStartDate ?? invite?.leaseStartDate,
  );
  const cardRequired = allowedType === "card";
  const bankRequired = allowedType === "bank";

  const isBankMethod = (m: { type: string }) => m.type !== "card";
  const allowedMethods = methods.filter((m) =>
    allowedType === null
      ? true
      : allowedType === "bank"
        ? isBankMethod(m)
        : !isBankMethod(m),
  );
  const hasAllowedMethod = allowedMethods.length > 0;
  const selectedMethod = methods.find((m) => m.id === selectedPayment) ?? null;
  const isBankSelected =
    selectedMethod !== null && isBankMethod(selectedMethod);

  useEffect(() => {
    if (methods.length === 0) return;
    // Only ever auto-select from the allowed set; ignore a default of the
    // wrong type.
    const pool = allowedMethods;
    if (pool.length === 0) {
      // Nothing usable (e.g. bank required, none on file). Clear any stale
      // selection so the "Pay" button stays disabled.
      if (selectedPayment !== null) setSelectedPayment(null);
      return;
    }
    if (selectedPayment !== null) {
      // Re-validate: if the previously-selected method is no longer of the
      // allowed type, move to one that is.
      const stillValid = pool.some((m) => m.id === selectedPayment);
      if (!stillValid) {
        const def = pool.find((m) => m.isDefault) ?? pool[0];
        setSelectedPayment(def?.id ?? null);
      }
      return;
    }
    const def = pool.find((m) => m.isDefault) ?? pool[0];
    if (def) setSelectedPayment(def.id);
  }, [methods, selectedPayment, allowedMethods]);

  // Reset the ACH checkbox whenever the selected method changes. A user
  // switching from a bank to a card and back should re-acknowledge.
  useEffect(() => {
    setMandateAccepted(false);
  }, [selectedPayment]);

  const handlePurchase = async () => {
    if (!uuid) return;
    if (!fullyCredited) {
      if (!selectedPayment) return;
      if (isBankSelected && !mandateAccepted) return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setPurchasing(true);
    setError(null);
    try {
      const res = await purchaseSdaInvite(
        uuid,
        selectedPayment as number,
        isBankSelected && !fullyCredited ? true : undefined,
      );
      setResult(res.data);
    } catch (err: any) {
      // Full error stays in the browser console for debugging; the user
      // sees a generic, translated message regardless of what the server
      // returned. Avoids leaking Stripe internals (idempotency keys,
      // mandate states, etc.) into the UI.
      console.error("SDA purchase failed:", err);
      setError(t("sda.payment.errorDefault"));
    } finally {
      setPurchasing(false);
      inFlightRef.current = false;
    }
  };

  // An admin credit comes off the quote total, exactly as the server
  // applies it, so this page and the charge can't disagree.
  const quoteTotal = quote ? Number(quote.total) : null;
  const grantedCredit =
    invite?.creditAppliedAt != null ? 0 : Number(invite?.creditAmount ?? 0);
  const creditApplied =
    quoteTotal != null && grantedCredit > 0
      ? Math.min(grantedCredit, quoteTotal)
      : 0;
  const amountDue =
    quoteTotal != null ? quoteTotal - creditApplied : null;
  // Nothing to charge: no payment method or ACH mandate is required, and
  // the server skips Stripe entirely.
  const fullyCredited = amountDue === 0 && creditApplied > 0;
  const totalLabel = amountDue != null ? formatCurrency(amountDue) : "—";

  // Once we have a transaction status, render the success screen.
  // `succeeded` (typically card) → all-set state; anything else still rendering
  // here means `processing` (typically ACH/bank). `failed`/`cancelled` keep
  // the user on the payment form below with an error banner.
  if (
    result &&
    (result.status === "succeeded" || result.status === "processing")
  ) {
    return (
      <div>
        <SDAGoBack skipConfirm />
        <SDATestModeBanner />
        <SDASteps current={7} />
        <SDASuccess
          status={result.status}
          policyName={result.policyName ?? null}
          renewalDate={result.renewalDate ?? invite?.leaseEndDate ?? null}
        />
      </div>
    );
  }

  return (
    <div>
      <SDAGoBack />

      <SDATestModeBanner />

      <SDASteps current={7} />

      <div className="rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-primary">
            {t("sda.payment.title")}
          </h3>
          <p className="mt-0.5 text-sm text-muted">
            {t("sda.payment.subtitle")}
          </p>
        </div>

        {error && (
          <div className="mx-5 mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
            {error}
          </div>
        )}
        {result &&
          result.status !== "succeeded" &&
          result.status !== "processing" && (
            <div className="mx-5 mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
              {t("sda.payment.errorDefault")}
            </div>
          )}

        <div className="p-5">
          {/* Nothing to charge — the method picker, the required-type
              banner and the "add a card" CTA are all about a payment that
              isn't happening, so none of them are shown. */}
          {fullyCredited && (
            <div className="mb-6 rounded-lg border-2 border-success/30 bg-success/10 p-5">
              <p className="mb-1 text-sm font-semibold text-success">
                No payment needed
              </p>
              <p className="mb-0 text-sm text-gray-700">
                A credit covers this purchase in full. Confirm below and your
                coverage starts right away — you won&rsquo;t be charged and
                no payment method is required.
              </p>
            </div>
          )}

          {!fullyCredited && (
          <>
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-primary">
              {t("sda.payment.selectMethod")}
            </p>
            <button
              type="button"
              className="text-sm font-medium text-info underline hover:text-sky-600 cursor-pointer"
              onClick={() => navigate("/payment-methods")}
            >
              {t("sda.payment.manageMethods")}
            </button>
          </div>

          {allowedType !== null && (
            <div className="mb-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-warning">
              {cardRequired
                ? t("sda.payment.cardOnlyBanner")
                : t(
                    "sda.payment.bankOnlyBanner",
                    "Because coverage starts more than 5 days from now, this payment must be made from a bank account.",
                  )}
            </div>
          )}

          {methodsLoading ? (
            <p className="text-sm text-muted">
              {t("sda.payment.loadingMethods")}
            </p>
          ) : allowedType !== null && !hasAllowedMethod ? (
            // No method of the required type — surface a dedicated CTA to
            // add one instead of an all-disabled picker.
            <div className="mb-6 rounded-lg border-2 border-dashed border-amber-200 bg-amber-50/50 p-5 text-center">
              <p className="mb-3 text-sm text-gray-700">
                {cardRequired
                  ? t("sda.payment.cardOnlyAddPrompt")
                  : t(
                      "sda.payment.bankOnlyAddPrompt",
                      "You'll need a bank account to complete this purchase. Add one to continue.",
                    )}
              </p>
              <button
                type="button"
                className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer"
                onClick={() =>
                  cardRequired ? setShowAddCard(true) : setShowAddBank(true)
                }
              >
                {cardRequired
                  ? t("sda.payment.addCard")
                  : t("sda.payment.addBank", "Add bank account")}
              </button>
            </div>
          ) : (
            <div className="mb-6">
              <PaymentMethodPicker
                methods={methods}
                selectedId={selectedPayment}
                onSelect={setSelectedPayment}
                emptyMessage={t("sda.payment.emptyMethods")}
                allowedType={allowedType}
                disabledHint={
                  cardRequired
                    ? t("sda.payment.cardOnlyHint")
                    : bankRequired
                      ? t(
                          "sda.payment.bankOnlyHint",
                          "Coverage starts more than 5 days from now — pay from a bank account.",
                        )
                      : undefined
                }
              />
            </div>
          )}
          </>
          )}

          {isBankSelected && !fullyCredited && (
            <label className="mb-4 flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer"
                checked={mandateAccepted}
                onChange={(e) => setMandateAccepted(e.target.checked)}
                disabled={purchasing}
              />
              <span>
                {t("sda.payment.achAuthorization", {
                  amount: totalLabel,
                })}
              </span>
            </label>
          )}

          <div className="rounded-lg bg-gray-50 p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">
              {t("sda.payment.summary")}
            </p>
            {creditApplied > 0 && quoteTotal != null && (
              <>
                <div className="mb-1 flex items-center justify-between text-sm text-gray-700">
                  <span>Quote</span>
                  <span>{formatCurrency(quoteTotal)}</span>
                </div>
                <div className="mb-2 flex items-center justify-between text-sm font-medium text-success">
                  <span>Credit applied</span>
                  <span>−{formatCurrency(creditApplied)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">
                {fullyCredited ? "Nothing to pay" : t("sda.payment.total")}
              </span>
              <span className="text-xl font-bold text-primary">
                {totalLabel}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-between border-t border-gray-100 px-5 py-4">
          <button
            className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
            onClick={() => navigate(`/sda/${uuid}/signatures`)}
          >
            {t("sda.payment.back")}
          </button>
          <div className="flex items-center gap-2">
            <SDATestSkipButton
              onSkip={() =>
                setResult({
                  transactionUuid: "test-skip",
                  status: "succeeded",
                  amount: 0,
                  paymentIntentId: "test-skip",
                })
              }
            />
            <button
              className="rounded-lg bg-success px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              disabled={
                purchasing ||
                (!fullyCredited &&
                  (!selectedPayment ||
                    (isBankSelected && !mandateAccepted)))
              }
              onClick={handlePurchase}
            >
              {purchasing
                ? t("sda.payment.purchasing")
                : t("sda.payment.purchase")}
            </button>
          </div>
        </div>
      </div>

      <AddCardModal
        open={showAddCard}
        onClose={() => setShowAddCard(false)}
        onSaved={() => {
          setShowAddCard(false);
          refreshMethods();
        }}
      />

      <AddBankModal
        open={showAddBank}
        onClose={() => setShowAddBank(false)}
        onConnected={() => {
          setShowAddBank(false);
          refreshMethods();
        }}
      />
    </div>
  );
};

export default SDAPayment;
